import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import SecurityIcon from '@mui/icons-material/Security';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../../utils/api';

function PermissionsChips({ permissions }) {
  const list = permissions || [];
  if (list.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No permissions
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
      {list.slice(0, 40).map((p) => (
        <Chip key={p.id} size="small" variant="outlined" label={p.code} sx={{ fontFamily: 'monospace', fontSize: 10 }} />
      ))}
      {list.length > 40 ? <Chip size="small" label={`+${list.length - 40} more`} /> : null}
    </Box>
  );
}

function UserAccessBlock({ user }) {
  if (!user) {
    return (
      <Alert severity="warning" sx={{ py: 0.5 }}>
        No system login — assign a user on the employee record to see groups and roles.
      </Alert>
    );
  }
  if (user.is_admin) {
    return (
      <Alert severity="info" sx={{ py: 0.5, mb: 1 }}>
        <strong>Administrator</strong> — full application access (individual role permissions are not listed).
      </Alert>
    );
  }
  const groups = user.groups || [];
  if (groups.length === 0) {
    return <Typography variant="body2" color="text.secondary">Not in any access group.</Typography>;
  }
  return (
    <Stack spacing={1.25}>
      {groups.map((g) => (
        <Paper key={g.id} variant="outlined" sx={{ p: 1.25, bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
            <GroupWorkIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Group: {g.name}
            </Typography>
            {!g.is_active ? <Chip size="small" label="Inactive" color="warning" /> : null}
          </Stack>
          {(g.roles || []).length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No roles on this group.
            </Typography>
          ) : (
            (g.roles || []).map((r) => (
              <Box key={r.id} sx={{ ml: 1, mt: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <SecurityIcon fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Role: {r.name}
                  </Typography>
                  {!r.is_active ? <Chip size="small" label="Inactive" /> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  Permissions ({(r.permissions || []).length})
                </Typography>
                <PermissionsChips permissions={r.permissions} />
              </Box>
            ))
          )}
        </Paper>
      ))}
    </Stack>
  );
}

function EmployeeRow({ emp }) {
  const c = emp.contact;
  const u = emp.user;
  const label = c?.name || u?.name || emp.employee_code || 'Employee';
  return (
    <Accordion disableGutters variant="outlined" sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ py: 0.25 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
          <Chip size="small" label={emp.employee_code} variant="outlined" />
          {emp.status ? <Chip size="small" label={emp.status} /> : null}
          {u ? (
            <Chip size="small" icon={<VpnKeyIcon sx={{ fontSize: '14px !important' }} />} label={u.email} variant="outlined" />
          ) : (
            <Chip size="small" label="No login" color="warning" variant="outlined" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, bgcolor: 'action.hover' }}>
        <UserAccessBlock user={u} />
      </AccordionDetails>
    </Accordion>
  );
}

export default function UserAccessMap() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/users/access-map');
      setData(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load access map');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        User Access Map
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Hosting company (account type <strong>hosting_company</strong>) → employees linked via contact–account
        membership → system user (if any) → <strong>groups</strong> → <strong>roles</strong> →{' '}
        <strong>permissions</strong>. Employees whose contact is not linked to any hosting company appear under{' '}
        <em>Other</em>.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {(data?.hosting_trees || []).map((block) => (
            <Accordion key={block.hosting_company.id || block.hosting_company.name} defaultExpanded disableGutters sx={{ mb: 1.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BusinessIcon color="primary" />
                  <Typography sx={{ fontWeight: 800 }}>{block.hosting_company.name}</Typography>
                  {block.hosting_company.account_code ? (
                    <Typography variant="caption" color="text.secondary">
                      {block.hosting_company.account_code}
                    </Typography>
                  ) : null}
                  <Chip size="small" label={`${(block.employees || []).length} employee(s)`} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {(block.employees || []).length === 0 ? (
                  <Typography color="text.secondary">No employee HR rows for contacts linked to this hosting company.</Typography>
                ) : (
                  (block.employees || []).map((emp) => <EmployeeRow key={emp.id} emp={emp} />)
                )}
              </AccordionDetails>
            </Accordion>
          ))}

          {data?.other_bucket ? (
            <Accordion defaultExpanded sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontWeight: 800 }}>{data.other_bucket.hosting_company.name}</Typography>
                  <Chip size="small" label={`${(data.other_bucket.employees || []).length} employee(s)`} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {(data.other_bucket.employees || []).length === 0 ? (
                  <Typography color="text.secondary">None.</Typography>
                ) : (
                  (data.other_bucket.employees || []).map((emp) => <EmployeeRow key={emp.id} emp={emp} />)
                )}
              </AccordionDetails>
            </Accordion>
          ) : null}
        </>
      )}
    </Box>
  );
}
